import {
  commonChartConfig,
  MAX_OTHER_BAR_TOOLTIP_ITEMS,
  groupOtherData,
  getTooltipPositionFn,
  chartLegendConfig,
  chartGridConfig,
  pageTextStyleColor,
} from '../common-chart-config';
import * as echarts from 'echarts';
import { DefaultLabelFormatterCallbackParams } from 'echarts';
import { LegendTooltipFormatterParams } from '../utils';
import {
  DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS,
  OTHER_SEGMENT_FROM_BACKEND_NAME,
  OTHER_SEGMENT_NAME,
} from '../../consts';
import { getDataValuesAsArray, getDataWithTotal } from './timelineUtils';
import { getLegendItemWidth, legendTooltipFormatter } from '../utils';
import { isEmpty, isUndefined } from 'lodash';
import { TimeLineData, HexColor, WidgetTypes } from '@ardoq/api-types';
import { formatNumber, Locale } from '@ardoq/locale';
import { StackedBarChartWidgetProps } from './types';
import { StackedBarChartRenderData } from '../renderTypes';
import { type I18nLocale, t } from '@ardoq/i18n';

type StackedBarChartDataSegments = {
  combinedSeries: {
    name: string;
    values: (string | number | null)[][];
    total?: number;
  }[];
  isOtherSeries: (index: number) => boolean;
  otherSeries: {
    name: string;
    values: (string | number | null)[][];
    total?: number;
  }[];
};

type GetStackedBarChartWidgetPropsParam = {
  widgetRenderData: StackedBarChartRenderData;
  colors: Record<string, HexColor>;
  userLocale: Locale;
  i18nLocale: I18nLocale;
};

export const getStackedBarChartWidgetProps = ({
  widgetRenderData,
  ...props
}: GetStackedBarChartWidgetPropsParam): StackedBarChartWidgetProps & {
  viewType: WidgetTypes.STACKED_BAR_CHART;
} => {
  return {
    ...widgetRenderData,
    ...props,
    width: widgetRenderData.layout.w,
    numberSlices:
      widgetRenderData.numberSlices ??
      DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS,
  };
};

export const getStackedBarChartData = (
  data: TimeLineData,
  numberOfSegments: number,
  userLocale: Locale
): StackedBarChartDataSegments => {
  const dataSeries = getDataWithTotal(data).sort((a, b) => {
    if (a.name === OTHER_SEGMENT_FROM_BACKEND_NAME) {
      return 1; // Make sure the backend-aggregate is joined with the FE one.
    } else if (b.name === OTHER_SEGMENT_FROM_BACKEND_NAME) {
      return -1;
    }
    return 0;
  });

  const [mainSeries, otherSeries, isOtherSeries] = groupOtherData(
    dataSeries,
    numberOfSegments
  );
  const otherSeriesValue = otherSeries.reduce<{
    name: string;
    values: Array<{ value: number; _time: string }>;
    total: number;
  }>(
    (memo, { values }) => {
      memo.values = values.map(({ _time, value }, index) => ({
        _time,
        value:
          value === null && (memo.values[index]?.value ?? null) === null
            ? 0
            : (value ?? 0) + (memo.values[index]?.value ?? 0),
      }));
      return memo;
    },
    { name: OTHER_SEGMENT_NAME, values: [], total: 0 }
  );
  otherSeriesValue.total = otherSeriesValue.values.reduce<number>(
    (acc, current) => acc + current.value,
    0
  );

  const combinedSeries = [
    ...getDataValuesAsArray(mainSeries, userLocale),
    ...(otherSeriesValue.total
      ? getDataValuesAsArray([otherSeriesValue], userLocale)
      : []),
  ];

  return {
    otherSeries: getDataValuesAsArray(otherSeries, userLocale),
    isOtherSeries,
    combinedSeries: combinedSeries,
  };
};

export const getStackedBarChartOptions = (
  { isOtherSeries, otherSeries, combinedSeries }: StackedBarChartDataSegments,
  colors: Record<string, HexColor>,
  widgetWidth: number,
  dashboardContainerRef: HTMLDivElement | null,
  userLocale: Locale,
  i18nLocale: I18nLocale
): echarts.EChartsOption => {
  const includingLabel = t(
    i18nLocale,
    'dashboard:stackedBarChart.tooltip.including',
    'Including'
  );
  const andMoreLabel = t(
    i18nLocale,
    'dashboard:stackedBarChart.tooltip.andMoreSections',
    '... and more bar sections'
  );
  const series = combinedSeries.map(({ name, values }, seriesIndex) => {
    const seriesColor = colors?.[name];
    const isTopOfStack = seriesIndex === combinedSeries.length - 1;
    const borderRadius = isTopOfStack ? [2, 2, 0, 0] : undefined;
    const itemStyle = {
      ...(seriesColor ? { color: seriesColor } : {}),
      ...(borderRadius ? { borderRadius } : {}),
    };

    return {
      name,
      data: values,
      symbol: 'circle',
      symbolSize: 5,
      type: 'bar',
      stack: 'stack1',
      itemStyle: !isEmpty(itemStyle) ? itemStyle : undefined,
    };
  }) as echarts.EChartsOption['series'];

  return {
    ...commonChartConfig,
    tooltip: {
      trigger: 'axis',
      confine: false,
      extraCssText: 'width:auto; white-space:normal; max-width: 80vw',
      position: getTooltipPositionFn(dashboardContainerRef),
      formatter: data => `
      ${(data as DefaultLabelFormatterCallbackParams[])[0].name}<br />
      ${(data as DefaultLabelFormatterCallbackParams[])
        .filter(item => (item.value as [string, number])[1] !== null)
        .map(item => {
          const otherSeriesFiltered = otherSeries.filter(
            otherItem => otherItem.values[item.dataIndex][1] !== null
          );

          return isOtherSeries(
            !isUndefined(item.seriesIndex) ? item.seriesIndex : 0
          ) && otherSeriesFiltered.length
            ? `
        <b>${(item.value as [string, number])[1]}</b> ${item.seriesName}<br />
        <span style='visibility: hidden;'>${
          item.marker
        }</span><i>${includingLabel}</i><br />
          ${otherSeriesFiltered
            .slice(0, MAX_OTHER_BAR_TOOLTIP_ITEMS)
            .map(
              otherItem =>
                `<span style='visibility: hidden;'>${item.marker}</span><b>${
                  otherItem.values[item.dataIndex][1]
                }</b> ${
                  otherItem.name === OTHER_SEGMENT_FROM_BACKEND_NAME
                    ? OTHER_SEGMENT_NAME
                    : otherItem.name
                }`
            )
            .join('<br />')}

            ${
              otherSeriesFiltered.length > MAX_OTHER_BAR_TOOLTIP_ITEMS
                ? `<br /><i>${andMoreLabel}</i>`
                : ''
            }
        `
            : `${item.marker}<b>${(item.value as [string, number])[1]}</b> ${
                item.seriesName
              }`;
        })
        .join('<br />')}
      `,
    },
    legend: {
      ...chartLegendConfig,

      bottom: undefined,
      top: 8,
      right: 0,
      left: undefined,
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
      textStyle: {
        width: getLegendItemWidth(combinedSeries.length, widgetWidth),
        overflow: 'truncate',
        color: pageTextStyleColor,
      },
      tooltip: {
        formatter: (params: LegendTooltipFormatterParams) =>
          legendTooltipFormatter(params, widgetWidth),
        show: true,
      },
    },
    xAxis: {
      type: 'category',
      axisTick: {
        alignWithLabel: true,
      },
      axisLine: { lineStyle: { color: pageTextStyleColor } },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: number) =>
          formatNumber(value, 'default', userLocale),
      },
    },
    series,
    grid: { ...chartGridConfig, top: '36px', bottom: '16px' }, // 36 on the top to make room for legend
  };
};
